'use client';

import { useEffect, useRef, useState } from 'react';
import { ROUTES } from '@/constants/application-urls';
import { jump } from '@/styles/animation';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useToast } from '@/utils/providers/ToastProvider';
import { CartResult } from '@/types/gearApi/Cart';
import { ApiResult } from '@/types/gearApi/Result';
import LocalizedLink from '@/components/LocalizedLink';

const CartNavigation = ({
	cartLabel,
	cartResult,
	errorMessage,
}: {
	cartLabel: string;
	cartResult: ApiResult<CartResult> | null;
	errorMessage: string;
}) => {
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
	const { showToast } = useToast();
	const quantity = cartResult?.data?.totalLineItemQuantity;

	const prevQuantity = useRef(quantity);
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		let timeoutId: number | null = null;

		if (quantity && quantity !== prevQuantity.current) {
			setIsAnimating(true);
			timeoutId = window.setTimeout(() => setIsAnimating(false), 300);
			prevQuantity.current = quantity;
		}
		return () => {
			if (timeoutId !== null) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [quantity]);

	useEffect(() => {
		if (cartResult && !cartResult.ok) {
			showToast({
				message: errorMessage,
				severity: 'error',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorMessage, cartResult]);

	return (
		<LocalizedLink
			href={ROUTES.CART}
			underline="none"
			color="black.main"
			sx={{
				lineHeight: '18px',
				marginLeft: {
					xs: 'auto',
					md: '0',
				},
			}}
			data-testid="cart-navigation-icon"
		>
			<Box display="flex" alignItems="center" gap="12px">
				<Badge
					badgeContent={quantity}
					color="error"
					sx={{
						'& .MuiBadge-badge': {
							animation: isAnimating ? `${jump} 0.3s ease` : 'none',
						},
					}}
					data-testid="badge-icon"
				>
					<ShoppingCartOutlinedIcon />
				</Badge>
				{!isMobileOrTablet && cartLabel}
			</Box>
		</LocalizedLink>
	);
};

export default CartNavigation;
